import { useState } from 'react';
import { Alert, Badge, Button, FileInput, Group, Select, Stack, Table, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { EGITIM_ICE_AKTARMA_SONUC_ETIKETI } from '@isg/validation';
import type { TrainingImportReport } from '@isg/validation';
import type { z } from 'zod';
import type { trainingOptionsSchema } from '@isg/validation';
import {
  indirmeyiBaslat,
  useCiktiIndir,
  useEgitimDevirOnizle,
  useEgitimDevirYukle,
} from '../api/queries';
import { ANLAM } from '../theme';

type EgitimOptions = z.output<typeof trainingOptionsSchema>;

/**
 * SİSTEM ÖNCESİ EĞİTİM YÜKLEMESİ — İKİ ADIMLI PANEL (SÖ-1; 02.11.2026 — Kapsam
 * §14 SÖ-1). Tur 5'in standart panelinin (`EgitimYuklemePaneli`) yanında durur;
 * **aynı kapı** (`egitimYuklemeKapisi` — tenant `egitim_kaydi` listesi + bahçe).
 *
 * Adım 1 — dosya → **konu eşleme** (önizleme; sunucu kayıt AÇMAZ): dosyadaki
 * benzersiz konu metinleri + önerilen tür + sayılar. Kullanıcı türü düzeltir;
 * boş bıraktığı konu **Diğer**e gider (orijinal metin kaydın notuna).
 * ⚠️ Formlarda önizleme reddedilmişti; burada eşleme **satır başına değil konu
 * başına** ve **yetkinlik doğurduğu** için gerekli (ürün sahibi kararı 7).
 *
 * Adım 2 — **Yükle**: aynı dosya + eşleme → `POST trainings/import?kip=sistem_oncesi`.
 * Sonuç: toplam · yüklenen · eşleşmeyen kişi · hatalı tarih · istihdam dışı ·
 * uyarılar; satır sonuçları `.csv` olarak indirilebilir (ekrandaki verinin
 * aynısı — ikinci bir sunucu sorgusu yoktur, çıktı motoruna GİRMEZ: rapor tek
 * seferlik bir yanıttır, yeniden koşulacak sorgusu yok).
 *
 * ⚠️ Ekran hiçbir sayıyı kendisi hesaplamaz; özet sunucunun `devir` bloğundan.
 * ⚠️ K-72 bilgi satırı (*"istihdam başlangıcından önce"*) UYARI değil BİLGİDİR
 * (rozetsiz, renksiz — GA-4 deseni): davranış doğru, kılavuza girer.
 */
export function EgitimDevirYuklemePaneli({
  options,
}: {
  options: EgitimOptions | undefined;
}): React.JSX.Element {
  const indir = useCiktiIndir();
  const onizle = useEgitimDevirOnizle();
  const yukle = useEgitimDevirYukle();
  const [dosya, setDosya] = useState<File | null>(null);
  /** konu metni → seçilen tür (null = Diğer). Önizleme gelince önerilerle doldurulur. */
  const [esleme, setEsleme] = useState<Record<string, string | null>>({});

  const turSecenekleri = (options?.egitimTurleri ?? []).map((t) => ({ value: t.id, label: t.ad }));
  const onizleme = onizle.data;

  function dosyaSec(secilen: File | null): void {
    setDosya(secilen);
    setEsleme({});
    onizle.reset();
    yukle.reset();
  }

  function cozumle(): void {
    if (!dosya) return;
    onizle.mutate(
      { dosya },
      {
        onSuccess: (sonuc) => {
          setEsleme(
            Object.fromEntries(sonuc.konular.map((k) => [k.metin, k.onerilenTurId] as const)),
          );
        },
      },
    );
  }

  function yukleyiBaslat(): void {
    if (!dosya || !onizleme) return;
    yukle.mutate({
      dosya,
      konuEslemesi: onizleme.konular.map((k) => ({
        metin: k.metin,
        egitimTuruId: esleme[k.metin] ?? null,
      })),
    });
  }

  return (
    <Stack gap="sm" data-testid="egitim-devir-paneli">
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="egitim-devir-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/trainings/import/template?kip=sistem_oncesi',
              yedekAd: 'sistem-oncesi-egitim-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="egitim-devir-dosya"
          placeholder="Eğitim geçmişi dosyasını seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={dosyaSec}
          clearable
          size="xs"
          w={320}
        />
        <Button
          size="xs"
          variant="default"
          data-testid="egitim-devir-onizle"
          disabled={!dosya}
          loading={onizle.isPending}
          onClick={cozumle}
        >
          Dosyayı çözümle
        </Button>
      </Group>
      {onizle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="egitim-devir-onizleme-hata">
          {onizle.error.message}
        </Alert>
      )}
      {onizleme && !yukle.data && (
        <Stack gap="xs" data-testid="egitim-devir-onizleme">
          <Group gap="xs" data-testid="egitim-devir-onizleme-sayilar">
            <Badge color={ANLAM.bilgi} variant="light">
              Satır: {onizleme.satirSayisi}
            </Badge>
            <Badge
              color={onizleme.eslesmeyenKisi > 0 ? ANLAM.dikkat : ANLAM.bilgi}
              variant="light"
              data-testid="egitim-devir-eslesmeyen-kisi"
            >
              Eşleşmeyen kişi: {onizleme.eslesmeyenKisi}
            </Badge>
            <Badge
              color={onizleme.hataliTarih > 0 ? ANLAM.dikkat : ANLAM.bilgi}
              variant="light"
              data-testid="egitim-devir-hatali-tarih"
            >
              Hatalı tarih: {onizleme.hataliTarih}
            </Badge>
          </Group>
          {onizleme.taninmayanBasliklar.length > 0 && (
            <Text size="xs" c={ANLAM.dikkat} data-testid="egitim-devir-taninmayan-basliklar">
              Yok sayılan sütunlar: {onizleme.taninmayanBasliklar.join(' · ')}
            </Text>
          )}
          <Table data-testid="egitim-devir-konu-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Excel&apos;deki konu</Table.Th>
                <Table.Th w={90}>Satır</Table.Th>
                <Table.Th w={360}>Eğitim türü</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {onizleme.konular.map((konu, i) => (
                <Table.Tr key={konu.metin} data-testid="egitim-devir-konu-satiri">
                  <Table.Td>{konu.metin}</Table.Td>
                  <Table.Td>{konu.satirSayisi}</Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      data-testid={`egitim-devir-konu-secici-${i}`}
                      placeholder="Diğer (eşlenmedi)"
                      searchable
                      clearable
                      data={turSecenekleri}
                      value={esleme[konu.metin] ?? null}
                      onChange={(deger) => setEsleme((eski) => ({ ...eski, [konu.metin]: deger }))}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group>
            <Button
              size="xs"
              data-testid="egitim-devir-yukle"
              loading={yukle.isPending}
              onClick={yukleyiBaslat}
            >
              Yükle
            </Button>
          </Group>
        </Stack>
      )}
      {yukle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="egitim-devir-yukleme-hata">
          {yukle.error.message}
        </Alert>
      )}
      {yukle.data && (
        <Stack gap="xs" data-testid="egitim-devir-sonuc">
          <Group gap="xs" data-testid="egitim-devir-ozeti">
            <Badge color={ANLAM.bilgi} variant="light">
              Toplam: {yukle.data.toplam}
            </Badge>
            <Badge color={ANLAM.olumlu} variant="light">
              Yüklenen: {yukle.data.eklenen}
            </Badge>
            <Badge color={ANLAM.bilgi} variant="light">
              Oturum: {yukle.data.oturum}
            </Badge>
            {yukle.data.devir && (
              <>
                <Badge color={ANLAM.engel} variant="light">
                  Eşleşmeyen kişi: {yukle.data.devir.eslesmeyenKisi}
                </Badge>
                <Badge color={ANLAM.engel} variant="light">
                  Hatalı tarih: {yukle.data.devir.hataliTarih}
                </Badge>
                <Badge color={ANLAM.dikkat} variant="light">
                  İstihdam dışı: {yukle.data.devir.istihdamDisi}
                </Badge>
              </>
            )}
            <Badge color={ANLAM.dikkat} variant="light">
              Uyarı: {yukle.data.satirlar.filter((s) => s.uyarilar.length > 0).length}
            </Badge>
          </Group>
          {yukle.data.devir && yukle.data.devir.istihdamOncesi > 0 && (
            <Text size="sm" data-testid="egitim-devir-istihdam-oncesi">
              {yukle.data.devir.istihdamOncesi} kayıt istihdam başlangıcından önce — uygunlukta
              sayılmaz (işe girişte tekrar istenir).
            </Text>
          )}
          <Group>
            <Button
              size="xs"
              variant="default"
              data-testid="egitim-devir-sonuc-indir"
              onClick={() => indirmeyiBaslat(sonucCsv(yukle.data), 'egitim-yukleme-sonucu.csv')}
            >
              Sonuçları indir (.csv)
            </Button>
          </Group>
          <YuklemeSonucTablosu rapor={yukle.data} testId="egitim-devir-sonuc-tablosu" />
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Satır sonuç tablosu — standart ve sistem öncesi panelin ORTAK gövdesi.
 * UYARI ≠ HATA: uyarılı satır yeşil değil dikkat rengiyle listelenir.
 */
export function YuklemeSonucTablosu({
  rapor,
  testId,
}: {
  rapor: TrainingImportReport;
  testId: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <Table striped data-testid={testId}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={70}>Satır</Table.Th>
          <Table.Th w={260}>Oturum</Table.Th>
          <Table.Th w={140}>Sonuç</Table.Th>
          <Table.Th>Açıklama</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rapor.satirlar.map((satir) => (
          <Table.Tr key={satir.satir} data-testid={`${testId}-satiri`}>
            <Table.Td>{satir.satir}</Table.Td>
            <Table.Td>
              {satir.trainingSessionId ? (
                <Text
                  size="sm"
                  c={ANLAM.bilgi}
                  style={{ cursor: 'pointer' }}
                  onClick={() => void navigate(`/egitimler/${satir.trainingSessionId}`)}
                >
                  {satir.oturumAnahtari}
                </Text>
              ) : (
                satir.oturumAnahtari
              )}
            </Table.Td>
            <Table.Td>
              <Badge
                variant="light"
                color={
                  satir.sonuc === 'hata'
                    ? ANLAM.engel
                    : satir.uyarilar.length > 0
                      ? ANLAM.dikkat
                      : ANLAM.olumlu
                }
              >
                {EGITIM_ICE_AKTARMA_SONUC_ETIKETI[satir.sonuc]}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Stack gap={2}>
                {satir.ad && (
                  <Text size="sm" fw={500}>
                    {satir.ad}
                  </Text>
                )}
                {satir.mesaj && <Text size="sm">{satir.mesaj}</Text>}
                {satir.uyarilar.map((u) => (
                  <Text key={u} size="xs" c={ANLAM.dikkat}>
                    {u}
                  </Text>
                ))}
              </Stack>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/** Ekrandaki satır sonuçlarının `.csv` hâli (ayraç `;` — Türkçe Excel varsayılanı, BOM'lu). */
function sonucCsv(rapor: TrainingImportReport): Blob {
  const hucre = (deger: string | number | null): string => {
    const metin = deger === null ? '' : String(deger);
    return /[;"\n]/.test(metin) ? `"${metin.replace(/"/g, '""')}"` : metin;
  };
  const satirlar = [
    ['Satır', 'Oturum', 'Sonuç', 'Kişi', 'Açıklama', 'Uyarılar'].join(';'),
    ...rapor.satirlar.map((s) =>
      [
        hucre(s.satir),
        hucre(s.oturumAnahtari),
        hucre(EGITIM_ICE_AKTARMA_SONUC_ETIKETI[s.sonuc]),
        hucre(s.ad),
        hucre(s.mesaj),
        hucre(s.uyarilar.join(' | ')),
      ].join(';'),
    ),
  ];
  return new Blob([`\uFEFF${satirlar.join('\n')}`], { type: 'text/csv;charset=utf-8' });
}
